import { Component, OnInit, signal, computed, inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { StockDeliveryRecord, OutboundDeliveryItem } from '../../stock-delivery.config';
import { StockDeliveryService } from '../../stock-delivery.service';
import { TableColumn } from '@shared/components/data-table/data-table.component';
import { OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_COLUMNS } from '../add/outbound-delivery-order-add.config';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { BASE_PATH, OutboundDeliveryOrderControllerService, ResultRecordControllerService } from '@app/api';
import { environment } from '@environments/environment';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import {
  formatDeliveryDate,
  getDeliveryStatusSeverity,
  getDeliveryStatusLabel,
  getOutboundTypeLabel,
  mapStatusToString,
} from '../../delivery.helpers';
import { formatDateWithLocale } from '@shared/utils/date-formatter.util';

@Component({
  selector: 'app-delivery-view',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ToastModule,
    DataTableComponent,
    AppButtonComponent,
    ProgressSpinnerModule,
    TagModule,
  ],
  providers: [
    MessageService,
    {
      provide: BASE_PATH,
      useValue: environment.stockURL,
    },
    OutboundDeliveryOrderControllerService,
    ResultRecordControllerService,
    StockDeliveryService,
  ],
  templateUrl: './delivery-order-view.component.html',
  styleUrl: './delivery-order-view.component.css',
})
export class DeliveryViewComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private deliveryService = inject(StockDeliveryService);
  private translateService = inject(TranslateService);
  private destroy$ = new Subject<void>();

  // Data signals
  loading = signal(false);
  formData = signal<StockDeliveryRecord | null>(null);
  productDetails = signal<any[]>([]);
  productTableColumns = signal<TableColumn[]>([]);
  scheduledDeliveryTime = signal<Date | null>(null);
  
  // Outbound type signal
  outType = signal<string | undefined>(undefined);
  isTransferOrder = computed(() => this.outType() === 'transfer-out-stock');
  isSalesOrder = computed(() => this.outType() === 'order-sales-out-stock');

  // Page title
  pageTitle = computed(() => 'outbound_management.view.order_title');

  ngOnInit(): void {
    this.loadViewData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Navigation handlers
  onBackClick(): void {
    this.router.navigate(['/stock/delivery/delivery-order']);
  }

  private loadViewData(): void {
    this.loading.set(true);
    
    const id = this.route.snapshot.paramMap.get('id');
    
    if (!id) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
      });
      this.router.navigate(['/stock/delivery/delivery-order']);
      return;
    }

    this.loadDeliveryInformData(id);
  }

  private loadDeliveryInformData(id: string): void {
    // Use real API to load basic data
    this.deliveryService.getDeliveryDetail(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: StockDeliveryRecord) => {
          if (data) {
            this.configureDeliveryInformForm(data);
            // Load product details separately
            this.loadProductDetails(id);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: this.translateService.instant('common.error'),
              detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
            });
            this.router.navigate(['/stock/delivery/delivery-order']);
            this.loading.set(false);
          }
        },
        error: (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.error'),
            detail: this.translateService.instant('outbound_management.messages.failed_to_load'),
          });
          this.router.navigate(['/stock/delivery/delivery-order']);
          this.loading.set(false);
        }
      });
  }

  private loadProductDetails(id: string): void {
    // Call API to get product details: /v1/outbound/delivery/order/{id}/details
    this.deliveryService.getDeliveryOrderDetails(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items: OutboundDeliveryItem[]) => {
          this.initializeProductDetails(items);
          this.loading.set(false);
        },
        error: (error: any) => {
          // If product details fail, still show basic info but log error
          this.productDetails.set([]);
          this.loading.set(false);
        }
      });
  }

  private configureDeliveryInformForm(data: StockDeliveryRecord): void {
    // Set outType signal for conditional rendering
    this.outType.set(data.outType);

    // Format data for display
    const formattedData: StockDeliveryRecord = {
      ...data,
      // Map outTypeMeaning if it's a raw API value, otherwise use as is
      outTypeMeaning: data.outTypeMeaning 
        ? this.getOutboundTypeLabel(data.outTypeMeaning)
        : (data.outType ? this.getOutboundTypeLabel(data.outType) : '-'),
      // Ensure status is mapped to string (in case it's still a number)
      status: mapStatusToString(data.status),
      qty: data.qty,
    };

    this.formData.set(formattedData);

    // Set scheduledDeliveryTime if available
    const scheduledTime = data.scheduledDeliveryTime;
    if (scheduledTime) {
      const date = typeof scheduledTime === 'string' 
        ? new Date(scheduledTime)
        : scheduledTime;
      if (date instanceof Date && !isNaN(date.getTime())) {
        this.scheduledDeliveryTime.set(date);
      }
    } else {
      // Reset to null if no scheduled time
      this.scheduledDeliveryTime.set(null);
    }
    // Product details will be loaded separately via loadProductDetails()
  }

  private initializeProductDetails(detailList: OutboundDeliveryItem[]): void {
    // Build columns: SPU code, SPU name, SKU code, SKU name, plannedQty (before quantity), quantity
    const baseColumns = OUTBOUND_DELIVERY_ORDER_PRODUCT_TABLE_COLUMNS.filter((col: TableColumn) => 
      col.prop !== 'quantity' && col.prop !== 'alreadyOutQty'
    );
    
    const productTableColumns: TableColumn[] = [
      ...baseColumns.map((col: TableColumn) => col),
      // Add plannedQty column before quantity
      {
        label: 'outbound_management.fields.planned_quantity',
        prop: 'plannedQty',
        width: 120,
        type: 'text',
        align: 'right',
      },
      // Add quantity column after plannedQty (số lượng xuất)
      {
        label: 'outbound_management.table.columns.quantity',
        prop: 'quantity',
        width: 120,
        type: 'text',
        align: 'right',
      },
      {
        label: 'lot_trace.lot',
        prop: 'batchId',
        width: 160,
        type: 'text',
      },
    ];

    // Format detailList with serial numbers
    const formattedDetails = detailList.map((item, index) => {
      // Shipped Quantity = alreadyOutQty || pickedQty
      const shippedQty = item.alreadyOutQty !== undefined && item.alreadyOutQty !== null 
        ? item.alreadyOutQty 
        : (item.pickedQty !== undefined && item.pickedQty !== null ? item.pickedQty : null);
      
      return {
        ...item,
        serialNumber: index + 1,
        quantity: shippedQty !== null && shippedQty !== undefined 
          ? this.formatNumber(shippedQty) 
          : '-',
        plannedQty: typeof item.plannedQty === 'number' ? this.formatNumber(item.plannedQty) : (typeof item.plannedQty !== 'undefined' ? String(item.plannedQty) : '-'),
      };
    });

    this.productTableColumns.set(productTableColumns);
    this.productDetails.set(formattedDetails);
  }

  // Helper methods for template
  getFieldValue(fieldName: string): any {
    const data = this.formData();
    if (!data) return null;
    const value = (data as any)[fieldName];
    // Return empty string as-is, don't convert to null
    return value !== undefined && value !== null ? value : null;
  }

  getWarehouseDisplay(codeField: string, nameField: string): string {
    const code = this.getFieldValue(codeField);
    const name = this.getFieldValue(nameField);
    
    if (code && name) {
      return `${code} - ${name}`;
    } else if (code) {
      return code;
    } else if (name) {
      return name;
    }
    return '-';
  }

  // Format receiver display - translate "Guest Customer" to Vietnamese
  getReceiverDisplay(receiver: string | null | undefined): string {
    if (!receiver) {
      return '-';
    }
    if (receiver === 'Guest Customer') {
      return this.translateService.instant('outbound_management.guest_customer');
    }
    return receiver;
  }

  // Get shipped quantity: alreadyOutQty || pickedQty
  getShippedQuantity(): string {
    const data = this.formData();
    if (!data) {
      return '-';
    }
    const alreadyOutQty = (data as any).alreadyOutQty;
    const pickedQty = this.getTotalPickedQty();
    
    if (alreadyOutQty !== undefined && alreadyOutQty !== null) {
      return this.formatNumber(alreadyOutQty);
    }
    if (pickedQty !== undefined && pickedQty !== null && pickedQty > 0) {
      return this.formatNumber(pickedQty);
    }
    return '-';
  }

  // Calculate total picked quantity from product details
  private getTotalPickedQty(): number | undefined {
    const details = this.productDetails();
    if (!details || details.length === 0) {
      return undefined;
    }
    const total = details.reduce((sum, item) => {
      const pickedQty = typeof item.pickedQty === 'number' ? item.pickedQty : 0;
      return sum + pickedQty;
    }, 0);
    return total > 0 ? total : undefined;
  }

  hasLogisticsInfo(): boolean {
    const logisticCompany = this.getFieldValue('logisticCompany');
    const logisticBusiness = this.getFieldValue('logisticBusiness');
    return !!(logisticCompany || logisticBusiness);
  }

  formatDate = formatDeliveryDate;
  formatDateOnly = (value: any): string => {
    const formatted = formatDateWithLocale(value, 'vi');
    return formatted || '-';
  };

  getStatusSeverity(status: string | number): string {
    const severity = getDeliveryStatusSeverity(status);
    return severity;
  }

  getStatusTagClass(status: string | number): string {
    const severity = this.getStatusSeverity(status);
    return `status-tag p-tag-${severity}`;
  }

  private getOutboundTypeLabel(outTypeMeaning: string): string {
    return getOutboundTypeLabel(outTypeMeaning, this.translateService);
  }

  getStatusLabel(status: string | number): string {
    const outType = this.getFieldValue('outType');
    return getDeliveryStatusLabel(status, this.translateService, true, outType);
  }

  private formatNumber(value: number | undefined): string {
    if (value === undefined || value === null) {
      return '-';
    }
    return value.toLocaleString('vi-VN');
  }
}
